import type { CSSProperties, PointerEvent } from 'react';
import type { Card, MatchView, Playability, Side } from '../../engine';
import { t } from '../../i18n';
import { cardLong, itemName, itemText } from '../../app/describe';
import { CardView, type CardSize, type CardState } from './CardView';

export interface HandCardInfo {
  readonly card: Card;
  readonly playability: Playability | null;
}

export function reasonText(p: Playability | null, v: MatchView): string | null {
  if (!p || p.playable) return null;
  if (p.reason === 'NO_MATCH') {
    const top = v.pile[v.pile.length - 1];
    return t('reason.NO_MATCH', { top: top ? `${top.face.color}${top.face.number}` : '—' });
  }
  return t(`reason.${p.reason}`);
}

/** What a card does when played: how it moves, or a Trick's effect; and its Stamp and Finish. */
export function moveHint(card: Card): string {
  const base = baseHint(card);
  const extras = [
    card.stamp ? `${itemName(card.stamp)}: ${itemText(card.stamp)}` : '',
    card.finish ? `${itemName(card.finish)}: ${itemText(card.finish)}` : '',
  ].filter(Boolean);
  return extras.length ? `${base}\n${extras.join('\n')}` : base;
}

function baseHint(card: Card): string {
  if (card.trick) return `${itemName(`trick.${card.trick}`)}: ${itemText(`trick.${card.trick}`)}`;
  if (card.kind === 'wild') return t('card.moveWild');
  const f = card.face!;
  const one = f.number === 1;
  switch (f.color) {
    case 'R':
      return one ? t('card.moveRed.one') : t('card.moveRed', { n: f.number });
    case 'B':
      return one ? t('card.moveBlue.one') : t('card.moveBlue', { n: f.number });
    case 'G':
      return t('card.moveGreen');
    case 'Y':
      return one ? t('card.moveYellow.one') : t('card.moveYellow', { n: f.number });
  }
}

/** A gentle fan: each card turned a little from the middle and lowered toward the ends. */
function fanStyle(i: number, n: number): CSSProperties {
  const mid = (n - 1) / 2;
  const d = n > 1 ? (i - mid) / Math.max(mid, 1) : 0;
  return {
    '--i': i,
    '--rot': `${(d * Math.min(n, 7) * 0.8).toFixed(2)}deg`,
    '--drop': `${(d * d * 5).toFixed(1)}px`,
  } as CSSProperties;
}

/** A card leans toward the mouse, and the light on it follows (mouse only). */
function tilt(e: PointerEvent<HTMLElement>): void {
  if (e.pointerType !== 'mouse') return;
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
  const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
  el.style.setProperty('--rx', `${((0.5 - y) * 18).toFixed(1)}deg`);
  el.style.setProperty('--ry', `${((x - 0.5) * 20).toFixed(1)}deg`);
  el.style.setProperty('--gx', `${Math.round(x * 100)}%`);
  el.style.setProperty('--gy', `${Math.round(y * 100)}%`);
}

function untilt(e: PointerEvent<HTMLElement>): void {
  for (const p of ['--rx', '--ry', '--gx', '--gy']) e.currentTarget.style.removeProperty(p);
}

/** The color a Tarnish House Rule dulls for a side's cards, if any (LEDGER.MD). */
const tarnishOf = (v: MatchView, side: Side): string | undefined =>
  v.config.ledger?.houseRules.find((r) => r.id === 'tarnish' && (r.side ?? 'S') === side)?.color;

/**
 * The viewer's hand, fanned: playable cards lifted and lit, the rest dimmed with why (§19.4).
 * Once a drafted turn has nothing left to play, every card is dimmed and says so.
 */
export function HandView({
  v,
  cards,
  selectedId,
  mode,
  interactive,
  candidates,
  lifted,
  burning = false,
  onPick,
  onHover,
  size = 'md',
}: {
  v: MatchView;
  cards: readonly HandCardInfo[];
  selectedId: string | null;
  mode: 'play' | 'recover' | 'idle' | 'spent';
  interactive: boolean;
  /** Cards to choose between for a move already aimed (drag and drop): they are lit. */
  candidates?: readonly string[];
  /** The card being dragged: dimmed in the hand. */
  lifted?: string | null;
  /** A Brazier is lit: whatever card goes next burns (LEDGER.MD §6). */
  burning?: boolean;
  onPick?: (cardId: string) => void;
  onHover?: (cardId: string | null) => void;
  size?: CardSize;
}) {
  if (cards.length === 0) return <p className="hand-empty dim">{t('match.noHand')}</p>;
  const n = cards.length;
  return (
    <ul
      className={`hand hand-${mode}`}
      aria-label={t('match.hand')}
      style={{ '--n': n, '--k': Math.max(1, n - 1) } as CSSProperties}
    >
      {cards.map(({ card, playability }, i) => {
        const spent = mode === 'spent';
        const reason = spent ? t('reason.TURN_SPENT') : reasonText(playability, v);
        const playable = mode === 'recover' ? true : !!playability?.playable;
        // Not taking input (the turn not yet under way), a card is greyed only when it is known
        // that it could not be played.
        const known = !!playability && !playability.playable && mode !== 'recover';
        const state: CardState =
          selectedId === card.id
            ? 'selected'
            : spent
              ? 'unplayable'
              : !interactive
                ? known
                  ? 'unplayable'
                  : 'neutral'
                : playable
                  ? 'playable'
                  : 'unplayable';
        const label = `${i + 1}. ${cardLong(card)}. ${moveHint(card)}${reason ? ` ${reason}` : ''}`;
        return (
          <li
            key={card.id}
            style={fanStyle(i, n)}
            data-card-id={card.id}
            className={`${candidates?.includes(card.id) ? 'is-candidate' : ''} ${lifted === card.id ? 'is-lifted' : ''} ${
              burning && playable && mode === 'play' ? 'is-burning' : ''
            }`}
          >
            <CardView
              face={card.face ?? null}
              wild={card.kind === 'wild'}
              stamp={card.stamp}
              stampValue={card.value}
              finish={card.finish}
              trick={card.trick}
              iou={card.iou}
              tarnished={!!card.face && card.face.color === tarnishOf(v, card.owner)}
              size={size}
              state={state}
              label={label}
              hint={reason ? `${moveHint(card)}\n${reason}` : moveHint(card)}
              pressed={selectedId === card.id}
              disabled={!interactive || !playable}
              onClick={interactive && onPick ? () => onPick(card.id) : undefined}
              onPointerEnter={() => onHover?.(card.id)}
              onPointerMove={tilt}
              onPointerLeave={(e) => {
                untilt(e);
                onHover?.(null);
              }}
            >
              <span className="card-key" aria-hidden="true">
                {i + 1}
              </span>
              {reason && known && (interactive ? mode === 'play' : mode === 'idle') && (
                <span className="card-ribbon" aria-hidden="true">
                  {t(`reason.short.${playability.reason}`)}
                </span>
              )}
              {burning && playable && mode === 'play' && (
                <span className="card-ribbon is-burn" aria-hidden="true">
                  {t('hand.burns')}
                </span>
              )}
              {spent && (
                <span className="card-ribbon" aria-hidden="true">
                  {t('reason.short.TURN_SPENT')}
                </span>
              )}
            </CardView>
          </li>
        );
      })}
    </ul>
  );
}
